import Cased from '@/components/ui/Cased'
import { Link } from 'react-router-dom'
import DocLayout from '../components/docs/DocLayout'
import CopyButton from '../components/docs/CopyButton'
import Icon from '../components/docs/Icon'
import Channels from '../components/docs/Channels'
import {
  agentInstructions,
  agentRules,
  machineAssets,
  SYSTEM_VERSION,
} from '../content/system'
export default function AgentsPage() {
  return (
    <DocLayout
      eyebrow="Agents"
      title="Give your tools the system."
      intro="Readable guidance for people. Structured instructions for agents. Both built from the same definitions."
      wide
    >
      <aside className="doc-callout doc-callout--row">
        <Icon name="code" size={28} />
        <div>
          <p className="doc-callout__title">Context before code.</p>
          <p>
            Load the manifest, choose a mission theme and a pattern, and apply
            the rules. MDS gives an agent the design decisions and the checks to
            verify them.
          </p>
        </div>
        <span className="mds-tag mds-tag--outline">v{SYSTEM_VERSION}</span>
      </aside>
      <Channels />
      <section className="doc-section">
        <h2><Cased>Start with this instruction</Cased></h2>
        <p>
          Add these instructions to your project context, then describe the
          mission and the task you want to build.
        </p>
        <div className="doc-file">
          <div className="doc-file__bar">
            <span>
              <Icon name="file" size={15} /> agents.md
            </span>
            <CopyButton text={agentInstructions} />
          </div>
          <pre
            className="mds-code doc-file__body"
            tabIndex={0}
            role="region"
            aria-label="Agent instructions"
          >
            <code>{agentInstructions}</code>
          </pre>
        </div>
      </section>
      <section className="doc-section">
        <h2><Cased>The machine-readable toolkit</Cased></h2>
        <p>
          Plain files, stable paths and an explicit version. Download the
          resources into your project or read them from this site.
        </p>
        <ul className="doc-assets">
          {/* Per-mission design-tool files are downloaded from the Design Tools page. */}
          {machineAssets.filter((asset) => !asset.path.includes('{mission}')).map((asset) => (
            <li key={asset.path}>
              <a href={asset.path} download className="doc-asset">
                <span className="doc-asset__icon">
                  <Icon name={asset.kind === 'JSON' ? 'code' : 'file'} />
                </span>
                <span className="doc-asset__text">
                  <strong>{asset.name}</strong>
                  <span className="mds-muted">{asset.description}</span>
                </span>
                <span className="mds-tag mds-tag--outline">{asset.kind}</span>
                <Icon name="download" size={18} />
              </a>
            </li>
          ))}
        </ul>
      </section>
      <section className="doc-section">
        <h2><Cased>Rules that travel with the work</Cased></h2>
        <ol className="pat-anatomy doc-rules">
          {agentRules.map((rule, index) => (
            <li key={rule.id}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <div>
                <h3><Cased>{rule.title}</Cased></h3>
                <p className="mds-muted">{rule.instruction}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>
      <section className="doc-section">
        <h2><Cased>From task to verified interface</Cased></h2>
        <ol className="doc-workflow">
          <li>
            <strong>Define the mission.</strong> Name the user, decision, data
            and operating context.
          </li>
          <li>
            <strong>Choose the theme.</strong> Set <code>data-mission</code> and
            support both <code>data-theme</code> values.
          </li>
          <li>
            <strong>Select the pattern.</strong> Read its anatomy, requirements
            and acceptance checks.
          </li>
          <li>
            <strong>Build all relevant states.</strong> Use components and
            semantic roles for a coherent result.
          </li>
          <li>
            <strong>Verify the experience.</strong> Check behavior, keyboard
            access, contrast and reflow. Record human review.
          </li>
        </ol>
        <p>
          <Link to="/patterns" className="mds-button mds-button--secondary">
            Choose a pattern <Icon name="arrow" size={16} />
          </Link>
        </p>
      </section>
    </DocLayout>
  )
}
